import { createSSRSafeStorage } from './createSSRSafeStorage';

/**
 * Key used to store the flag that determines navigation behavior
 */
const ALLOW_HISTORY_ACCESS_KEY = 'allowHistoryAccess';

// SSR-safe check for browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Creates an SSR-safe localStorage instance for navigation utilities
 */
const storage = createSSRSafeStorage(isBrowser ? window.localStorage : undefined);

/**
 * Checks if history access prevention is enabled via localStorage.
 * This determines whether to use window.location.replace() (which prevents back navigation)
 * or window.location.href/assign() (which allows back navigation).
 *
 * By default, history access is prevented unless explicitly allowed via localStorage.
 *
 * @returns true if history access should be prevented, false otherwise
 */
export function shouldPreventHistoryAccess(): boolean {
  // Add a try/catch to handle the case where the storage is not available just in case.
  try {
    return storage.getItem(ALLOW_HISTORY_ACCESS_KEY) !== 'true';
  } catch (error) {
    return true;
  }
}

/**
 * Navigates to a URL with conditional history replacement based on localStorage setting.
 * Uses window.location.replace() if history access prevention is enabled,
 * otherwise uses window.location.href for normal navigation.
 *
 * @param url - The URL to navigate to
 */
export function navigateWithConditionalReplace(url: string): void {
  if (shouldPreventHistoryAccess()) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

/**
 * Assigns a URL with conditional history replacement based on localStorage setting.
 * Uses window.location.replace() if history access prevention is enabled,
 * otherwise uses window.location.assign() for normal navigation.
 *
 * @param url - The URL to assign/navigate to
 */
export function assignWithConditionalReplace(url: string): void {
  if (shouldPreventHistoryAccess()) {
    window.location.replace(url);
  } else {
    window.location.assign(url);
  }
}
